import * as THREE from 'three';
import type { CollisionWorld } from '@/world/collision';

/**
 * Botsingen tussen dingen die bewegen.
 *
 * `CollisionWorld` beschrijft de stad: gebouwen, kades, lantaarnpalen. Die staat stil en
 * wordt één keer opgebouwd. Alles wat rijdt en loopt zit hier, want dat verandert elke
 * simulatiestap en hoort niet in een grid dat je per frame zou moeten herbouwen.
 *
 * Twee soorten lichamen, met bewust verschillend gedrag:
 *
 * - **Voertuigen blokkeren.** Een auto is een muur voor wie te voet is; je loopt er niet
 *   doorheen en er ook niet tegenaan tot je erin zit.
 * - **Personages duwen.** Ze mogen elkaar niet overlappen, maar ze houden elkaar ook niet
 *   tegen: bij overlap schuiven ze uit elkaar. Hard blokkeren zou je in de smalle
 *   havenstraatjes laten vastlopen achter je eigen soldaten, en juist daar speelt missie 1.
 */

export interface ActorBody {
  /** Wordt ter plekke aangepast; dit is dezelfde vector die het personage zelf gebruikt. */
  readonly position: THREE.Vector2;
  readonly radius: number;
  /** Doden blokkeren niets meer — je loopt over een lijk heen. */
  readonly alive: boolean;
  /** Alles wat in deze wereld loopt kan geraakt worden — door kogels of door een bumper. */
  damage(amount: number): void;
}

export interface VehicleBody {
  readonly position: THREE.Vector2;
  readonly rotation: number;
  readonly collisionHalfWidth: number;
  readonly collisionHalfLength: number;
  /** Een uitgebrande auto blijft staan en blijft dus blokkeren. */
  readonly state: string;
}

/** Hoe ver personages minimaal uit elkaar blijven, bovenop hun eigen stralen. */
const ACTOR_MARGIN = 0.04;

/**
 * Beide personages nemen de helft van de overlap.
 *
 * Alleen de bewegende partij verplaatsen werkt niet: loop je recht op iemand af, dan wijst
 * de duw precies tegen je looprichting in en sta je stil. Dat is hard blokkeren via een
 * omweg, en juist dat wilden we niet. Door beide kanten te verzetten schuif je iemand opzij
 * en kom je erlangs.
 */
const PUSH_SHARE = 0.5;

export class BodyRegistry {
  readonly #actors: ActorBody[] = [];
  readonly #vehicles: VehicleBody[] = [];

  addActor(actor: ActorBody): void {
    if (!this.#actors.includes(actor)) this.#actors.push(actor);
  }

  removeActor(actor: ActorBody): void {
    const index = this.#actors.indexOf(actor);
    if (index >= 0) this.#actors.splice(index, 1);
  }

  addVehicle(vehicle: VehicleBody): void {
    if (!this.#vehicles.includes(vehicle)) this.#vehicles.push(vehicle);
  }

  removeVehicle(vehicle: VehicleBody): void {
    const index = this.#vehicles.indexOf(vehicle);
    if (index >= 0) this.#vehicles.splice(index, 1);
  }

  get actors(): readonly ActorBody[] {
    return this.#actors;
  }

  get vehicles(): readonly VehicleBody[] {
    return this.#vehicles;
  }

  clear(): void {
    this.#actors.length = 0;
    this.#vehicles.length = 0;
  }

  /** Levende personages die deze doos raken. Gebruikt door het aanrijden. */
  actorsInBox(
    x: number,
    z: number,
    rotation: number,
    halfWidth: number,
    halfLength: number,
    skip?: ActorBody,
  ): ActorBody[] {
    const hits: ActorBody[] = [];
    for (const actor of this.#actors) {
      if (actor === skip || !actor.alive) continue;
      if (circleOverlapsBox(actor.position, actor.radius, x, z, rotation, halfWidth, halfLength)) {
        hits.push(actor);
      }
    }
    return hits;
  }
}

/**
 * Het register van het draaiende spel, net als `bus` uit `EventBus`. Er is precies één
 * wereld, dus precies één register. Tests maken hun eigen `BodyRegistry` en geven die mee
 * aan `moveActor`, zodat ze niet van deze gedeelde staat afhangen.
 */
export const bodies = new BodyRegistry();

/**
 * Verplaats een personage en los alle botsingen op: eerst de stad, dan de auto's, dan de
 * andere mensen.
 *
 * De statische stap gaat via `CollisionWorld.moveCircle()` — die glijdt al netjes langs
 * muren en heeft zijn eigen tests. Hier komt alleen bij wat beweegt.
 */
export function moveActor(
  actor: ActorBody,
  delta: THREE.Vector2,
  world: CollisionWorld,
  bodies: BodyRegistry,
): THREE.Vector2 {
  // In substappen, want elke botsingstoets kijkt naar wáár je uitkomt, niet naar de lijn
  // ernaartoe. Eén grote stap springt daardoor dwars door een muur of een auto heen. Bij
  // 60 Hz is een stap ruim vijf centimeter en valt dit nooit op, maar deze functie hoort
  // niet stilzwijgend van de framerate af te hangen.
  const distance = delta.length();
  const maxStep = Math.max(0.05, actor.radius * 0.9);
  const steps = Math.min(64, Math.max(1, Math.ceil(distance / maxStep)));

  for (let i = 0; i < steps; i++) {
    const before = tempBefore.copy(actor.position);
    tempStep.set(delta.x / steps, delta.y / steps);
    world.moveCircle(actor.position, tempStep, actor.radius);
    blockOnVehicles(actor, before, bodies);
    // De duw uit een auto kan je in een gevel zetten; `moveCircle` heeft daar geen zicht op
    // omdat die alleen de gevraagde stap toetst.
    world.resolveOverlap(actor.position, actor.radius);
  }

  separateFromActors(actor, bodies, world);
  return actor.position;
}

/**
 * Duw een personage uit de auto's waar hij in staat.
 *
 * Als hij er niet uit te duwen valt — klem tussen een auto en een muur — gaat hij terug
 * naar waar hij vandaan kwam. Vaststaan op je vorige plek is vervelend; door een auto heen
 * glijden is erger.
 */
function blockOnVehicles(actor: ActorBody, before: THREE.Vector2, bodies: BodyRegistry): void {
  for (const vehicle of bodies.vehicles) {
    const push = boxPushOut(
      actor.position,
      actor.radius,
      vehicle.position.x,
      vehicle.position.y,
      vehicle.rotation,
      vehicle.collisionHalfWidth,
      vehicle.collisionHalfLength,
    );
    if (!push) continue;

    actor.position.add(push);
    // Nog steeds ergens in een auto? Dan is er geen vrije uitweg; val terug.
    for (const other of bodies.vehicles) {
      if (
        circleOverlapsBox(
          actor.position,
          actor.radius,
          other.position.x,
          other.position.y,
          other.rotation,
          other.collisionHalfWidth,
          other.collisionHalfLength,
        )
      ) {
        actor.position.copy(before);
        return;
      }
    }
  }
}

/**
 * @param world Nodig omdat de duw beide kanten op werkt: wie opzij geschoven wordt kan in een
 *              muur belanden, en dan is hij daar niet meer uit te krijgen. Dat was de laatste
 *              route waarlangs je in een gebouw kon eindigen.
 */
function separateFromActors(actor: ActorBody, bodies: BodyRegistry, world: CollisionWorld): void {
  if (!actor.alive) return;

  for (const other of bodies.actors) {
    if (other === actor || !other.alive) continue;

    const dx = actor.position.x - other.position.x;
    const dz = actor.position.y - other.position.y;
    const minDistance = actor.radius + other.radius + ACTOR_MARGIN;
    const distanceSq = dx * dx + dz * dz;
    if (distanceSq >= minDistance * minDistance) continue;

    const distance = Math.sqrt(distanceSq);
    if (distance < 1e-4) {
      // Precies op elkaar: kies een vaste richting, anders deelt niemand door nul en
      // blijven ze eeuwig in elkaar staan.
      actor.position.x += minDistance * PUSH_SHARE;
      other.position.x -= minDistance * PUSH_SHARE;
      world.resolveOverlap(actor.position, actor.radius);
      world.resolveOverlap(other.position, other.radius);
      continue;
    }
    const overlap = (minDistance - distance) * PUSH_SHARE;
    const pushX = (dx / distance) * overlap;
    const pushZ = (dz / distance) * overlap;
    actor.position.x += pushX;
    actor.position.y += pushZ;
    other.position.x -= pushX;
    other.position.y -= pushZ;
    world.resolveOverlap(actor.position, actor.radius);
    world.resolveOverlap(other.position, other.radius);
  }
}

/**
 * Duw een personage uit een doos weg. Geeft terug of hij verplaatst is.
 *
 * Gebruikt door het aanrijden: onder de dodelijke snelheid schuif je iemand opzij in plaats
 * van hem te overrijden.
 */
export function shoveActorFromBox(
  actor: ActorBody,
  boxX: number,
  boxZ: number,
  rotation: number,
  halfWidth: number,
  halfLength: number,
): boolean {
  const push = boxPushOut(actor.position, actor.radius, boxX, boxZ, rotation, halfWidth, halfLength);
  if (!push) return false;
  actor.position.add(push);
  return true;
}

/** Raakt een cirkel deze geroteerde rechthoek? */
export function circleOverlapsBox(
  centre: THREE.Vector2,
  radius: number,
  boxX: number,
  boxZ: number,
  rotation: number,
  halfWidth: number,
  halfLength: number,
): boolean {
  const local = toBoxSpace(centre, boxX, boxZ, rotation);
  const closestX = THREE.MathUtils.clamp(local.x, -halfWidth, halfWidth);
  const closestZ = THREE.MathUtils.clamp(local.y, -halfLength, halfLength);
  const dx = local.x - closestX;
  const dz = local.y - closestZ;
  return dx * dx + dz * dz < radius * radius;
}

/**
 * De kortste verplaatsing die de cirkel uit de doos haalt, of `null` als hij er al buiten
 * ligt. Rekent in de lokale ruimte van de doos en draait het resultaat terug.
 */
function boxPushOut(
  centre: THREE.Vector2,
  radius: number,
  boxX: number,
  boxZ: number,
  rotation: number,
  halfWidth: number,
  halfLength: number,
): THREE.Vector2 | null {
  const local = toBoxSpace(centre, boxX, boxZ, rotation);
  const closestX = THREE.MathUtils.clamp(local.x, -halfWidth, halfWidth);
  const closestZ = THREE.MathUtils.clamp(local.y, -halfLength, halfLength);
  let dx = local.x - closestX;
  let dz = local.y - closestZ;
  const distanceSq = dx * dx + dz * dz;

  if (distanceSq >= radius * radius) return null;

  if (distanceSq > 1e-8) {
    const distance = Math.sqrt(distanceSq);
    const push = radius - distance;
    dx = (dx / distance) * push;
    dz = (dz / distance) * push;
  } else {
    // Middelpunt zit ín de doos: duw naar de dichtstbijzijnde zijde.
    const toRight = halfWidth - local.x;
    const toLeft = halfWidth + local.x;
    const toFront = halfLength - local.y;
    const toBack = halfLength + local.y;
    const smallest = Math.min(toRight, toLeft, toFront, toBack);
    dx = smallest === toRight ? toRight + radius : smallest === toLeft ? -(toLeft + radius) : 0;
    dz = smallest === toFront ? toFront + radius : smallest === toBack ? -(toBack + radius) : 0;
  }

  return fromBoxSpace(dx, dz, rotation);
}

/** Wereldpunt naar de lokale ruimte van een doos die om `rotation` gedraaid staat. */
function toBoxSpace(
  point: THREE.Vector2,
  boxX: number,
  boxZ: number,
  rotation: number,
): THREE.Vector2 {
  const dx = point.x - boxX;
  const dz = point.y - boxZ;
  const sin = Math.sin(rotation);
  const cos = Math.cos(rotation);
  // De voertuigrotatie draait de lengteas naar (sin, cos); dit is de inverse daarvan.
  return tempLocal.set(dx * cos - dz * sin, dx * sin + dz * cos);
}

function fromBoxSpace(x: number, z: number, rotation: number): THREE.Vector2 {
  const sin = Math.sin(rotation);
  const cos = Math.cos(rotation);
  return new THREE.Vector2(x * cos + z * sin, -x * sin + z * cos);
}

const tempLocal = new THREE.Vector2();
const tempBefore = new THREE.Vector2();
const tempStep = new THREE.Vector2();
